import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import { oesApi, settingsApi } from '../services/api';
import { getScaleLevels } from '../utils/settingsScales';
import Layout from '../components/layout/Layout';
import ProgressMeter from '../components/ProgressMeter';
import AISuggestionReviewModal from '../components/AISuggestionReviewModal';
import { useLabels } from '../providers/LabelsProvider';
import { useT } from '../providers/I18nProvider';

// Common IT/OT systems (Other is special: opens custom input)
const IT_OT_SYSTEMS = [
  'SCADA', 'DCS', 'PLC', 'HMI', 'RTU', 'IT Infrastructure', 'Information Systems',
  '4G/5G', 'LAN/WAN', 'Wireless Networks', 'DNS Systems', 'Core Banking', 'ATMs', 'Payment Processing',
  'ERP', 'CRM', 'DBMS', 'Cloud Services', 'Radar Systems', 'ATC (Air Traffic Control)', 
  'Power Grid Systems', 'Water Management Systems', 'Transportation Systems', 'Medical Devices',
  'IoT Devices', 'Industrial Control Systems', 'Other'
];

// Helper function to convert bg color to border-l color
const getBorderColor = (bgColor) => {
  const colorMap = {
    'bg-green-500': 'border-l-green-500',
    'bg-blue-400': 'border-l-blue-400',
    'bg-yellow-500': 'border-l-yellow-500',
    'bg-orange-500': 'border-l-orange-500',
    'bg-red-600': 'border-l-red-600',
  };
  return colorMap[bgColor] || 'border-l-gray-400';
};

export default function OESAssessment() {
  const labels = useLabels();
  const t = useT();
  const navigate = useNavigate();
  const { oesId } = useParams();
  const location = useLocation();
  const [currentPhase, setCurrentPhase] = useState(0); // 0: Services (with Interdependency), 1: Processes, 2: Cyberattack Impact
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(!!(location.state?.loadExisting && oesId));
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Phase 1: Services (up to 5) - each service has 5 factors: user_count, frequency, replaceability, interdependency, time_to_impact
  const [services, setServices] = useState([
    { 
      service_name: '', 
      service_description: '', 
      user_count_score: 0, 
      frequency_score: 0, 
      replaceability_score: 0,
      interdependency_score: 0,
      time_to_impact_score: 0
    }
  ]);
  
  // Phase 2: Processes (up to 5)
  const [processes, setProcesses] = useState([
    { process_name: '', process_description: '', automation_level: 0, it_ot_systems: [] }
  ]);
  // "Other" custom IT/OT category: which process index shows the input, and current value
  const [otherInputProcessIndex, setOtherInputProcessIndex] = useState(null);
  const [otherInputValue, setOtherInputValue] = useState('');
  
  // Phase 3: Asset valuation (1-5 per IT/OT system from processes)
  const [assetValuations, setAssetValuations] = useState({}); // { assetName: 1-5 }

  // Phase 4: Cyberattack Impact (removed supply_chain_impact)
  const [cyberattackImpact, setCyberattackImpact] = useState({
    security_impact: 0,
    economy_impact: 0,
    health_impact: 0,
    privacy_impact: 0,
    notes: ''
  });

  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiReviewOpen, setAiReviewOpen] = useState(false);
  const [aiReviewPhase, setAiReviewPhase] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [scales, setScales] = useState(null);

  const role = localStorage.getItem('role') || '';
  const isAdmin = ['global_admin', 'admin', 'assessment_admin'].includes(role);

  const phases = [t('assessment.servicesAssessment'), t('assessment.criticalProcesses'), t('assessment.assetValuation'), t('assessment.impactAssessment')];
  const phaseApiNames = ['services', 'processes', 'asset_valuation', 'cyberattack'];

  // Distinct IT/OT asset names from all processes (for Asset valuation step)
  const getDistinctAssets = () => {
    const seen = new Set();
    (processes || []).forEach((p) => {
      (p.it_ot_systems || []).forEach((name) => {
        if (name && name.trim()) seen.add(name.trim());
      });
    });
    return Array.from(seen).sort();
  };

  // When entering Asset valuation step, default any missing asset to 5 so validation passes and user can adjust
  useEffect(() => {
    if (currentPhase !== 2) return;
    const distinct = getDistinctAssets();
    if (distinct.length === 0) return;
    setAssetValuations((prev) => {
      let next = { ...prev };
      let changed = false;
      distinct.forEach((name) => {
        if (next[name] == null || next[name] < 1 || next[name] > 5) {
          next[name] = 5;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [currentPhase]); // Intentionally not depending on getDistinctAssets/processes to avoid overwriting user edits

  const ASSET_VALUATION_OPTIONS = [
    { value: 1, label: t('assessment.veryLow') },
    { value: 2, label: t('assessment.low') },
    { value: 3, label: t('assessment.moderate') },
    { value: 4, label: t('assessment.high') },
    { value: 5, label: t('assessment.veryHigh') },
  ];

  useEffect(() => {
    settingsApi.getForCurrentUser()
      .then((r) => setScales(r.data?.scales ?? {}))
      .catch(() => setScales({}));
  }, []);

  const userCountLevels = getScaleLevels(scales, 'user_count_levels');
  const frequencyLevels = getScaleLevels(scales, 'frequency_levels');
  const replaceabilityLevels = getScaleLevels(scales, 'replaceability_levels');
  const interdependencyLevels = getScaleLevels(scales, 'interdependency_levels');
  const timeToImpactLevels = getScaleLevels(scales, 'time_to_impact_levels');
  const automationLevels = getScaleLevels(scales, 'automation_levels');
  const cyberattackImpactLevels = getScaleLevels(scales, 'cyberattack_impact_levels');

  // Load existing assessment when "Update assessment" is used
  useEffect(() => {
    if (!location.state?.loadExisting || !oesId) return;
    const fetchExistingAssessment = async () => {
      try {
        setLoadingExisting(true);
        const response = await oesApi.getCurrentAssessment(oesId);
        const a = response.data;
        if (!a) return;
        if (a.services && a.services.length > 0) {
          setServices(a.services.map(s => ({
            service_name: s.service_name || '',
            service_description: s.service_description || '',
            user_count_score: s.user_count_score || 0,
            frequency_score: s.frequency_score || 0,
            replaceability_score: s.replaceability_score || 0,
            interdependency_score: s.interdependency_score || 0,
            time_to_impact_score: s.time_to_impact_score || 0
          })));
        }
        if (a.processes && a.processes.length > 0) {
          setProcesses(a.processes.map(p => ({
            process_name: p.process_name || '',
            process_description: p.process_description || '',
            automation_level: p.automation_level || 0,
            it_ot_systems: p.it_ot_systems ? p.it_ot_systems.split(',').map(s => s.trim()).filter(Boolean) : []
          })));
        }
        if (a.asset_valuations && typeof a.asset_valuations === 'object') {
          setAssetValuations(a.asset_valuations);
        }
        if (a.cyberattack_impact) {
          const c = a.cyberattack_impact;
          setCyberattackImpact({
            security_impact: c.security_impact || 0,
            economy_impact: c.economy_impact || 0,
            health_impact: c.health_impact || 0,
            privacy_impact: c.privacy_impact || 0,
            notes: c.notes || ''
          });
        }
      } catch (err) {
        console.error('Error loading existing assessment:', err);
      } finally {
        setLoadingExisting(false);
      }
    };
    fetchExistingAssessment();
  }, [oesId, location.state?.loadExisting]);
  
  const addService = () => {
    if (services.length < 5) {
      setServices([...services, { 
        service_name: '', 
        service_description: '', 
        user_count_score: 0, 
        frequency_score: 0, 
        replaceability_score: 0,
        interdependency_score: 0,
        time_to_impact_score: 0
      }]);
    }
  };

  const removeService = (index) => {
    if (services.length > 1) {
      setServices(services.filter((_, i) => i !== index));
    }
  };

  const updateService = (index, field, value) => {
    const updated = [...services];
    updated[index][field] = value;
    setServices(updated);
  };

  const addProcess = () => {
    if (processes.length < 5) {
      setProcesses([...processes, { process_name: '', process_description: '', automation_level: 0, it_ot_systems: [] }]);
    }
  };

  const removeProcess = (index) => {
    if (processes.length > 1) {
      setProcesses(processes.filter((_, i) => i !== index));
    }
  };

  const handleAssessWithAI = async () => {
    if (!oesId || aiLoading) return;
    setAiError('');
    setAiLoading(true);
    const phase = phaseApiNames[currentPhase];
    try {
      const res = await oesApi.aiSuggest(oesId, phase);
      const data = res.data;
      // Normalize AI response into the phase data shape
      let aiPhaseData = null;
      if (phase === 'services' && data.services?.length) {
        aiPhaseData = data.services.map(s => ({
          service_name: s.service_name || '',
          service_description: s.service_description || '',
          user_count_score: s.user_count_score || 0,
          frequency_score: s.frequency_score || 0,
          replaceability_score: s.replaceability_score || 0,
          interdependency_score: s.interdependency_score || 0,
          time_to_impact_score: s.time_to_impact_score || 0
        }));
      }
      if (phase === 'processes' && data.processes?.length) {
        aiPhaseData = data.processes.map(p => ({
          process_name: p.process_name || '',
          process_description: p.process_description || '',
          automation_level: p.automation_level || 0,
          it_ot_systems: Array.isArray(p.it_ot_systems) ? p.it_ot_systems : []
        }));
      }
      if (phase === 'cyberattack' && data) {
        aiPhaseData = {
          security_impact: data.security_impact ?? 0,
          economy_impact: data.economy_impact ?? 0,
          health_impact: data.health_impact ?? 0,
          privacy_impact: data.privacy_impact ?? 0,
          notes: data.notes ?? ''
        };
      }
      if (aiPhaseData) {
        setAiReviewPhase(phase);
        setAiSuggestion({ [phase]: aiPhaseData });
        setAiReviewOpen(true);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Could not get AI suggestion.';
      setAiError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setAiLoading(false);
    }
  };

  const handleApplyAiSuggestion = (merged) => {
    const phase = aiReviewPhase;
    if (phase === 'services' && merged.services) {
      setServices(merged.services);
    }
    if (phase === 'processes' && merged.processes) {
      setProcesses(merged.processes);
    }
    if (phase === 'cyberattack' && merged.cyberattack) {
      setCyberattackImpact(merged.cyberattack);
    }
    setAiReviewOpen(false);
    setAiSuggestion(null);
    setAiReviewPhase(null);
  };

  const getAiReviewCurrentData = () => {
    if (!aiReviewPhase) return {};
    if (aiReviewPhase === 'services') return { services };
    if (aiReviewPhase === 'processes') return { processes };
    if (aiReviewPhase === 'cyberattack') return { cyberattack: cyberattackImpact };
    return {};
  };

  const updateProcess = (index, field, value) => {
    const updated = [...processes];
    updated[index][field] = value;
    setProcesses(updated);
  };

  const toggleITOTSystem = (processIndex, system) => {
    if (system === 'Other') {
      setOtherInputProcessIndex(processIndex);
      setOtherInputValue('');
      return;
    }
    const updated = [...processes];
    const systems = updated[processIndex].it_ot_systems || [];
    if (systems.includes(system)) {
      updated[processIndex].it_ot_systems = systems.filter(s => s !== system);
    } else {
      updated[processIndex].it_ot_systems = [...systems, system];
    }
    setProcesses(updated);
  };

  const addOtherITOTSystem = (processIndex) => {
    const trimmed = otherInputValue.trim();
    if (!trimmed) return;
    const updated = [...processes];
    const systems = updated[processIndex].it_ot_systems || [];
    if (systems.includes(trimmed)) {
      setOtherInputProcessIndex(null);
      setOtherInputValue('');
      return;
    }
    updated[processIndex].it_ot_systems = [...systems, trimmed];
    setProcesses(updated);
    setOtherInputProcessIndex(null);
    setOtherInputValue('');
  };

  const validatePhase = (phase) => {
    if (phase === 0) {
      // Validate services - all 5 factors required for each service
      for (const service of services) {
        if (!service.service_name.trim()) {
          return 'Please provide a name for all services';
        }
        if (service.user_count_score === 0 || 
            service.frequency_score === 0 || 
            service.replaceability_score === 0 ||
            service.interdependency_score === 0 ||
            service.time_to_impact_score === 0) {
          return 'Please complete all impact assessments for all services';
        }
      }
    } else if (phase === 1) {
      // Validate processes (IT/OT systems required to separate non IT/OT OESes from critical CII)
      for (const process of processes) {
        if (!process.process_name.trim()) {
          return 'Please provide a name for all processes';
        }
        if (process.automation_level === 0) {
          return 'Please specify automation level for all processes';
        }
        if (!(process.it_ot_systems && process.it_ot_systems.length > 0)) {
          return 'Please select or specify at least one IT/OT system for each process';
        }
      }
    } else if (phase === 2) {
      // Validate asset valuation: every distinct asset must have value 1-5
      const distinctAssets = getDistinctAssets();
      if (distinctAssets.length === 0) {
        return 'No IT/OT systems from processes. Complete the previous step first.';
      }
      for (const name of distinctAssets) {
        const v = assetValuations[name];
        if (v == null || v < 1 || v > 5) {
          return `Please set a value (1-5) for asset "${name}"`;
        }
      }
    } else if (phase === 3) {
      // Validate cyberattack impact (removed supply_chain_impact)
      if (cyberattackImpact.security_impact === 0 ||
          cyberattackImpact.economy_impact === 0 ||
          cyberattackImpact.health_impact === 0 ||
          cyberattackImpact.privacy_impact === 0) {
        return 'Please assess all cyberattack impact areas';
      }
    }
    return null;
  };

  const calculateProgress = () => {
    let completedPhases = 0;
    
    // Phase 1: Services with all 5 factors (33%)
    if (services.length > 0 && services.some(s => 
      s.service_name && 
      s.user_count_score > 0 && 
      s.frequency_score > 0 && 
      s.replaceability_score > 0 &&
      s.interdependency_score > 0 &&
      s.time_to_impact_score > 0
    )) {
      completedPhases++;
    }
    
    // Phase 2: Processes (66%) - name, automation, and at least one IT/OT system
    if (processes.length > 0 && processes.some(p => 
      p.process_name && p.automation_level > 0 && (p.it_ot_systems || []).length > 0
    )) {
      completedPhases++;
    }
    
    // Phase 3: Asset valuation
    const distinctAssets = getDistinctAssets();
    const assetValuationComplete = distinctAssets.length > 0 && distinctAssets.every(
      (name) => { const v = assetValuations[name]; return v != null && v >= 1 && v <= 5; }
    );
    if (assetValuationComplete) completedPhases++;

    // Phase 4: Cyberattack Impact
    if (cyberattackImpact.security_impact > 0 &&
        cyberattackImpact.economy_impact > 0 &&
        cyberattackImpact.health_impact > 0 &&
        cyberattackImpact.privacy_impact > 0) {
      completedPhases++;
    }
    
    return Math.round((completedPhases / 4) * 100);
  };

  useEffect(() => {
    const newProgress = calculateProgress();
    setProgress(newProgress);
    
    // Update progress on backend
    const updateProgress = async () => {
      try {
        const completedPhases = [];
        if (services.length > 0 && services.some(s => 
          s.service_name && s.user_count_score > 0 && s.frequency_score > 0 && s.replaceability_score > 0
        )) {
          completedPhases.push('services');
        }
        // Interdependency is now per-service, checked in services validation
        if (processes.length > 0 && processes.some(p => 
          p.process_name && p.automation_level > 0 && (p.it_ot_systems || []).length > 0
        )) {
          completedPhases.push('processes');
        }
        const distinctAssetsForProgress = getDistinctAssets();
        const assetValComplete = distinctAssetsForProgress.length > 0 && distinctAssetsForProgress.every(
          (name) => { const v = assetValuations[name]; return v != null && v >= 1 && v <= 5; }
        );
        if (assetValComplete) completedPhases.push('asset_valuation');
        if (cyberattackImpact.security_impact > 0 &&
            cyberattackImpact.economy_impact > 0 &&
            cyberattackImpact.health_impact > 0 &&
            cyberattackImpact.privacy_impact > 0) {
          completedPhases.push('cyberattack');
        }
        
        if (completedPhases.length > 0) {
          await oesApi.updateProgress(oesId, completedPhases);
        }
      } catch (err) {
        console.error('Error updating progress:', err);
        // Don't show error to user, progress update is non-critical
      }
    };
    
    // Debounce progress updates
    const timeoutId = setTimeout(updateProgress, 1000);
    return () => clearTimeout(timeoutId);
  }, [services, processes, assetValuations, cyberattackImpact, oesId]);

  const handleNext = () => {
    const validationError = validatePhase(currentPhase);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setAiError('');
    if (currentPhase < phases.length - 1) {
      setCurrentPhase(currentPhase + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    setError('');
    setAiError('');
    if (currentPhase > 0) {
      setCurrentPhase(currentPhase - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Prepare assessment data
      const assessmentData = {
        services: services.map(s => ({
          service_name: s.service_name,
          service_description: s.service_description || null,
          user_count_score: s.user_count_score,
          frequency_score: s.frequency_score,
          replaceability_score: s.replaceability_score,
          interdependency_score: s.interdependency_score,
          time_to_impact_score: s.time_to_impact_score
        })),
        processes: processes.map(p => ({
          process_name: p.process_name,
          process_description: p.process_description || null,
          automation_level: p.automation_level,
          it_ot_systems: p.it_ot_systems.join(', ') || null
        })),
        asset_valuations: Object.keys(assetValuations).length > 0 ? assetValuations : undefined,
        cyberattack_impact: {
          security_impact: cyberattackImpact.security_impact,
          economy_impact: cyberattackImpact.economy_impact,
          health_impact: cyberattackImpact.health_impact,
          privacy_impact: cyberattackImpact.privacy_impact,
          notes: cyberattackImpact.notes || null
        },
        notes: ''
      };

      await oesApi.createAssessment(oesId, assessmentData);
      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error creating assessment');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-eucnet-grey-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="mt-2 text-lg font-medium text-gray-900">{t('assessment.assessmentComplete')}</h3>
            <p className="mt-2 text-sm text-gray-500">{t('assessment.calculatingCriticality')}</p>
            <p className="mt-1 text-xs text-gray-400">{t('assessment.redirectingDashboard')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (loadingExisting) {
    return (
      <div className="min-h-screen bg-eucnet-grey-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-eucnet-blue mx-auto mb-4" />
          <p className="text-gray-600 font-medium">{t('assessment.loadingPreviousAnswers')}</p>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate(`/oes`)} className="text-eucnet-blue hover:text-eucnet-navy flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            {t('assessment.backToDashboard')}
        </button>
          
          {/* Progress Meter */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">{t('assessment.progress')}:</span>
            <ProgressMeter progress={progress} size={80} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-eucnet-primary to-eucnet-sky px-8 py-6">
            <h1 className="text-2xl font-bold text-white">{t('assessment.criticalityAssessment', { entity: labels.entityName })}</h1>
            <p className="text-white/90 mt-1">{t('assessment.phase1Subtitle')}</p>
          </div>

          {/* Progress Indicator */}
          <div className="px-8 pt-6 pb-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                {t('posture.step', { current: currentPhase + 1, total: phases.length })}: {phases[currentPhase]}
              </span>
              <span className="text-sm text-gray-500">
                {t('assessment.percentComplete', { percent: Math.round(((currentPhase + 1) / phases.length) * 100) })}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-eucnet-primary to-eucnet-sky h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentPhase + 1) / phases.length) * 100}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              {phases.map((phase, idx) => (
                <span key={idx} className={idx <= currentPhase ? 'text-eucnet-blue font-medium' : ''}>
                  {phase}
                </span>
              ))}
            </div>
          </div>

          {/* Phase Content */}
          <div className="px-8 py-6 max-h-[calc(100vh-300px)] overflow-y-auto">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Phase 1: Services */}
            {currentPhase === 0 && (
              <div className="space-y-6">
                <div>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h2 className="text-xl font-bold text-eucnet-navy">{t('assessment.servicesAssessment')}</h2>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={handleAssessWithAI}
                        disabled={aiLoading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-eucnet-sky hover:bg-eucnet-navy rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <AutoAwesome sx={{ fontSize: 18 }} />
                        {aiLoading ? t('assessment.analyzing') : t('assessment.assessWithAI')}
                      </button>
                    )}
                  </div>
                  {aiError && <p className="text-red-600 text-sm mb-2">{aiError}</p>}
                  <p className="text-gray-600 mb-4">
                    {t('assessment.servicesDesc')}
                  </p>
                </div>

                {services.map((service, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-eucnet-navy">{t('assessment.service')} {index + 1}</h3>
                      {services.length > 1 && (
                        <button
                          onClick={() => removeService(index)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          {t('common.remove')}
                        </button>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('assessment.serviceName')} *
                        </label>
                        <input
                          type="text"
                          value={service.service_name}
                          onChange={(e) => updateService(index, 'service_name', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky"
                          placeholder={t('assessment.serviceNamePlaceholder')}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('assessment.descriptionOptional')}
                        </label>
                        <textarea
                          value={service.service_description}
                          onChange={(e) => updateService(index, 'service_description', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky"
                          rows="2"
                          placeholder={t('assessment.descriptionPlaceholder')}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('assessment.reachLabel')} *
                        </label>
                        <div className="space-y-2">
                          {userCountLevels.map((level) => (
                            <button
                              key={level.value}
                              onClick={() => updateService(index, 'user_count_score', level.value)}
                              className={`w-full text-left p-3 rounded-lg border border-gray-200 transition-all ${
                                service.user_count_score === level.value
                                  ? `border-l-4 ${getBorderColor(level.color)} bg-gray-50`
                                  : 'border-l-4 border-l-gray-200 hover:border-l-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="font-semibold text-gray-900">{level.label}</div>
                                  <div className="text-sm text-gray-600">{level.desc}</div>
                                </div>
                                {service.user_count_score === level.value && (
                                  <svg className="w-5 h-5 text-eucnet-blue ml-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('assessment.frequencyLabel')} *
                        </label>
                        <div className="space-y-2">
                          {frequencyLevels.map((level) => (
                            <button
                              key={level.value}
                              onClick={() => updateService(index, 'frequency_score', level.value)}
                              className={`w-full text-left p-3 rounded-lg border border-gray-200 transition-all ${
                                service.frequency_score === level.value
                                  ? `border-l-4 ${getBorderColor(level.color)} bg-gray-50`
                                  : 'border-l-4 border-l-gray-200 hover:border-l-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="font-semibold text-gray-900">{level.label}</div>
                                  <div className="text-sm text-gray-600">{level.desc}</div>
                                </div>
                                {service.frequency_score === level.value && (
                                  <svg className="w-5 h-5 text-eucnet-blue ml-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('assessment.replaceabilityLabel')} *
                        </label>
                        <p className="text-xs text-gray-500 mb-2">
                          {t('assessment.replaceabilityDesc')}
                        </p>
                        <div className="space-y-2">
                          {replaceabilityLevels.map((level) => (
                <button
                  key={level.value}
                  onClick={() => updateService(index, 'replaceability_score', level.value)}
                  className={`w-full text-left p-3 rounded-lg border border-gray-200 transition-all ${
                    service.replaceability_score === level.value
                      ? `border-l-4 ${getBorderColor(level.color)} bg-gray-50`
                      : 'border-l-4 border-l-gray-200 hover:border-l-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{level.label}</div>
                      <div className="text-sm text-gray-600">{level.desc}</div>
                    </div>
                    {service.replaceability_score === level.value && (
                      <svg className="w-5 h-5 text-eucnet-blue ml-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('assessment.interdependencyLabel')} *
                        </label>
                        <p className="text-xs text-gray-500 mb-2">
                          {t('assessment.interdependencyDesc')}
                        </p>
                        <div className="space-y-2">
                          {interdependencyLevels.map((level) => (
                            <button
                              key={level.value}
                              onClick={() => updateService(index, 'interdependency_score', level.value)}
                              className={`w-full text-left p-3 rounded-lg border border-gray-200 transition-all ${
                                service.interdependency_score === level.value
                                  ? `border-l-4 ${getBorderColor(level.color)} bg-gray-50`
                                  : 'border-l-4 border-l-gray-200 hover:border-l-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="font-semibold text-gray-900">{level.label}</div>
                                  <div className="text-sm text-gray-600">{level.desc}</div>
                                </div>
                                {service.interdependency_score === level.value && (
                                  <svg className="w-5 h-5 text-eucnet-blue ml-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('assessment.timeToImpactLabel')} *
                        </label>
                        <p className="text-xs text-gray-500 mb-2">
                          {t('assessment.timeToImpactDesc')}
                        </p>
                        <div className="space-y-2">
                          {timeToImpactLevels.map((level) => (
                            <button
                              key={level.value}
                              onClick={() => updateService(index, 'time_to_impact_score', level.value)}
                              className={`w-full text-left p-3 rounded-lg border border-gray-200 transition-all ${
                                service.time_to_impact_score === level.value
                                  ? `border-l-4 ${getBorderColor(level.color)} bg-gray-50`
                                  : 'border-l-4 border-l-gray-200 hover:border-l-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="font-semibold text-gray-900">{level.label}</div>
                                  <div className="text-sm text-gray-600">{level.desc}</div>
                                </div>
                                {service.time_to_impact_score === level.value && (
                                  <svg className="w-5 h-5 text-eucnet-blue ml-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {services.length < 5 && (
                  <button
                    onClick={addService}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-eucnet-blue hover:text-eucnet-blue transition-colors"
                  >
                    {t('assessment.addAnotherService')}
                  </button>
                )}
              </div>
            )}

            {/* Phase 2: Processes */}
            {currentPhase === 1 && (
              <div className="space-y-6">
                <div>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h2 className="text-xl font-bold text-eucnet-navy">{t('assessment.criticalProcesses')}</h2>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={handleAssessWithAI}
                        disabled={aiLoading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-eucnet-sky hover:bg-eucnet-navy rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <AutoAwesome sx={{ fontSize: 18 }} />
                        {aiLoading ? t('assessment.analyzing') : t('assessment.assessWithAI')}
                      </button>
                    )}
                  </div>
                  {aiError && <p className="text-red-600 text-sm mb-2">{aiError}</p>}
                  <p className="text-gray-600 mb-4">
                    {t('assessment.processesDesc')}
                  </p>
                </div>

                {processes.map((process, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-eucnet-navy">{t('assessment.process')} {index + 1}</h3>
                      {processes.length > 1 && (
                        <button
                          onClick={() => removeProcess(index)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          {t('common.remove')}
                        </button>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('assessment.processName')} *
                        </label>
                        <input
                          type="text"
                          value={process.process_name}
                          onChange={(e) => updateProcess(index, 'process_name', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky"
                          placeholder={t('assessment.processNamePlaceholder')}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('assessment.descriptionOptional')}
                        </label>
                        <textarea
                          value={process.process_description}
                          onChange={(e) => updateProcess(index, 'process_description', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky"
                          rows="2"
                          placeholder={t('assessment.processDescriptionPlaceholder')}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('assessment.automationLevelLabel')} *
                        </label>
                        <p className="text-xs text-gray-500 mb-2">
                          {t('assessment.automationLevelDesc')}
                        </p>
                        <div className="space-y-2">
                          {automationLevels.map((level) => (
                            <button
                              key={level.value}
                              onClick={() => updateProcess(index, 'automation_level', level.value)}
                              className={`w-full text-left p-3 rounded-lg border border-gray-200 transition-all ${
                                process.automation_level === level.value
                                  ? `border-l-4 ${getBorderColor(level.color)} bg-gray-50`
                                  : 'border-l-4 border-l-gray-200 hover:border-l-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="font-semibold text-gray-900">{level.label}</div>
                                  <div className="text-sm text-gray-600">{level.desc}</div>
                                </div>
                                {process.automation_level === level.value && (
                                  <svg className="w-5 h-5 text-eucnet-blue ml-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('assessment.itOtSystemsLabel')} *
                        </label>
                        <p className="text-xs text-gray-500 mb-2">
                          {t('assessment.itOtSystemsDesc')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {IT_OT_SYSTEMS.filter(s => s !== 'Other').map((system) => (
                            <button
                              key={system}
                              type="button"
                              onClick={() => toggleITOTSystem(index, system)}
                              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                                (process.it_ot_systems || []).includes(system)
                                  ? 'bg-eucnet-blue text-white'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              {system}
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() => toggleITOTSystem(index, 'Other')}
                            className="px-3 py-1 rounded-full text-sm border-2 border-dashed border-gray-400 text-gray-700 hover:border-eucnet-blue hover:text-eucnet-blue transition-colors"
                          >
                            Other
                          </button>
                        </div>
                        {otherInputProcessIndex === index && (
                          <div className="mt-3 flex gap-2 items-center flex-wrap">
                            <input
                              type="text"
                              value={otherInputValue}
                              onChange={(e) => setOtherInputValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  addOtherITOTSystem(index);
                                }
                              }}
                              placeholder={t('assessment.enterCustomCategory')}
                              className="flex-1 min-w-[180px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky text-sm"
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => addOtherITOTSystem(index)}
                              className="px-4 py-2 bg-eucnet-blue text-white rounded-lg hover:bg-eucnet-navy text-sm font-medium"
                            >
                              {t('common.add')}
                            </button>
                            <button
                              type="button"
                              onClick={() => { setOtherInputProcessIndex(null); setOtherInputValue(''); }}
                              className="px-3 py-2 text-gray-600 hover:text-gray-800 text-sm"
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        )}
                        {(process.it_ot_systems || []).length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-gray-600 mb-1">{t('assessment.selected')}</p>
                            <div className="flex flex-wrap gap-2">
                              {(process.it_ot_systems || []).map((system) => (
                                <span
                                  key={system}
                                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-eucnet-blue text-white"
                                >
                                  {system}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updated = [...processes];
                                      updated[index].it_ot_systems = (updated[index].it_ot_systems || []).filter(s => s !== system);
                                      setProcesses(updated);
                                    }}
                                    className="ml-0.5 rounded-full p-0.5 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
                                    aria-label={`Remove ${system}`}
                                  >
                                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {processes.length < 5 && (
                  <button
                    onClick={addProcess}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-eucnet-blue hover:text-eucnet-blue transition-colors"
                  >
                    {t('assessment.addAnotherProcess')}
                  </button>
                )}
              </div>
            )}

            {/* Phase 3: Asset valuation */}
            {currentPhase === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-eucnet-navy mb-2">{t('assessment.assetValuation')}</h2>
                  <p className="text-gray-600 mb-4">
                    {t('assessment.assetValuationDesc')}
                  </p>
                </div>
                <div className="space-y-4">
                  {getDistinctAssets().map((assetName) => (
                    <div key={assetName} className="border border-gray-200 rounded-lg p-4 bg-gray-50 flex flex-wrap items-center justify-between gap-4">
                      <span className="font-medium text-eucnet-navy">{assetName}</span>
                      <div className="flex flex-wrap gap-2">
                        {ASSET_VALUATION_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setAssetValuations((prev) => ({ ...prev, [assetName]: opt.value }))}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                              (assetValuations[assetName] === opt.value)
                                ? 'bg-eucnet-blue text-white border-eucnet-blue'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-eucnet-sky hover:text-eucnet-blue'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Phase 4: Cyberattack Impact */}
            {currentPhase === 3 && (
              <div className="space-y-6">
                <div>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h2 className="text-xl font-bold text-eucnet-navy">{t('assessment.impactAssessment')}</h2>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={handleAssessWithAI}
                        disabled={aiLoading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-eucnet-sky hover:bg-eucnet-navy rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <AutoAwesome sx={{ fontSize: 18 }} />
                        {aiLoading ? t('assessment.analyzing') : t('assessment.assessWithAI')}
                      </button>
                    )}
                  </div>
                  {aiError && <p className="text-red-600 text-sm mb-2">{aiError}</p>}
                  <p className="text-gray-600 mb-4">
                    {t('assessment.impactDesc')}
                  </p>
                </div>

                <div className="space-y-6">
                  {[
                    { key: 'security_impact', label: t('assessment.impactNationalSecurity'), desc: t('assessment.impactSecurityDesc') },
                    { key: 'economy_impact', label: t('assessment.impactEconomy'), desc: t('assessment.impactEconomyDesc') },
                    { key: 'health_impact', label: t('assessment.impactHealth'), desc: t('assessment.impactHealthDesc') },
                    { key: 'privacy_impact', label: t('assessment.impactPrivacy'), desc: t('assessment.impactPrivacyDesc') },
                  ].map((impact) => (
                    <div key={impact.key} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                      <h3 className="text-lg font-semibold text-eucnet-navy mb-1">{impact.label}</h3>
                      <p className="text-sm text-gray-600 mb-4">{impact.desc}</p>
                      <div className="space-y-2">
                        {cyberattackImpactLevels.map((level) => (
                          <button
                            key={level.value}
                            onClick={() => setCyberattackImpact({ ...cyberattackImpact, [impact.key]: level.value })}
                            className={`w-full text-left p-3 rounded-lg border border-gray-200 transition-all ${
                              cyberattackImpact[impact.key] === level.value
                                ? `border-l-4 ${getBorderColor(level.color)} bg-gray-50`
                                : 'border-l-4 border-l-gray-200 hover:border-l-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900">{level.label}</div>
                                <div className="text-sm text-gray-600">{level.desc}</div>
                              </div>
                              {cyberattackImpact[impact.key] === level.value && (
                                <svg className="w-5 h-5 text-eucnet-blue ml-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('assessment.additionalNotes')}
                    </label>
                    <textarea
                      value={cyberattackImpact.notes}
                      onChange={(e) => setCyberattackImpact({ ...cyberattackImpact, notes: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky"
                      rows="4"
                      placeholder={t('assessment.additionalNotesPlaceholder')}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="px-8 py-6 bg-gray-50 border-t border-gray-200 flex justify-between">
            <button
              onClick={handleBack}
              disabled={currentPhase === 0}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('common.back')}
            </button>
            <button
              onClick={handleNext}
              disabled={loading}
              className="px-6 py-2 bg-gradient-to-r from-eucnet-primary to-eucnet-sky text-white rounded-lg hover:from-eucnet-navy hover:to-eucnet-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? t('common.processing') : currentPhase === phases.length - 1 ? t('assessment.calculateCriticality') : t('common.next')}
            </button>
          </div>
        </div>
      </div>
      <AISuggestionReviewModal
        isOpen={aiReviewOpen}
        onClose={() => { setAiReviewOpen(false); setAiSuggestion(null); setAiReviewPhase(null); }}
        phase={aiReviewPhase}
        currentData={getAiReviewCurrentData()}
        aiData={aiSuggestion}
        onApply={handleApplyAiSuggestion}
      />
    </Layout>
  );
}
